import type { PropertyFormData, StepId } from "../../../types";
import { shouldSkipStep } from "../../../utils/stepLogic";
import { formSteps } from "../../../config/formSteps";

export function isStepFilled(
  stepId: StepId,
  formData: Partial<PropertyFormData>
): boolean {
  switch (stepId) {
    case "property-type":
      return !!formData.propertyType;
    case "sub-type":
      return !!formData.subPropertyType;
    case "property-size":
      if (formData.propertyType === "Wohnung") {
        return !!formData.livingSpace;
      }
      return !!formData.plotSize;
    case "additional-size":
      if (formData.propertyType === "Wohnung") {
        return !!formData.rooms;
      }
      if (formData.propertyType === "Haus") {
        return !!formData.livingSpace;
      }
      if (formData.propertyType === "Gewerbe") {
        return !!formData.commercialArea;
      }
      return false;
    case "construction-year":
      return !!formData.constructionYear;
    case "property-features":
      return !!(
        formData.propertyFeatures && formData.propertyFeatures.length > 0
      );
    case "quality":
      return !!formData.quality;
    case "condition":
      return !!formData.condition;
    case "usage":
      return !!formData.usage;
    case "purpose":
      return !!formData.reason;
    case "timeline":
      return !!formData.timeline;
    case "address":
      return !!(formData.city || formData.street || formData.zip_code);
    case "contact":
      // Contact step is considered filled if we're on it (it's the last step)
      return false; // We'll handle this separately
    default:
      return false;
  }
}

export function getStepLabel(
  stepId: StepId,
  formData: Partial<PropertyFormData>
): string {
  switch (stepId) {
    case "property-type":
      return "Immobilienart";
    case "sub-type":
      return "Typ";
    case "property-size":
      if (formData.propertyType === "Wohnung") {
        return "Wohnfläche";
      }
      return "Grundstücksgröße";
    case "additional-size":
      if (formData.propertyType === "Wohnung") {
        return "Zimmer";
      }
      if (formData.propertyType === "Haus") {
        return "Wohnfläche";
      }
      if (formData.propertyType === "Gewerbe") {
        return "Wohn- und Gewerbefläche";
      }
      return "";
    case "construction-year":
      return "Baujahr";
    case "property-features":
      return "Ausstattung";
    case "quality":
      return "Qualität";
    case "condition":
      return "Zustand";
    case "usage":
      return "Nutzung";
    case "purpose":
      return "Grund";
    case "timeline":
      return "Zeitlicher Horizont";
    case "address":
      return "Standort";
    case "contact":
      return "Kontaktdaten";
    default:
      return "";
  }
}

export function getStepValue(
  stepId: StepId,
  formData: Partial<PropertyFormData>
): string {
  // Helper to format property features
  const formatFeatures = (features?: string[]): string => {
    if (!features || features.length === 0) return "";
    const featureLabels: Record<string, string> = {
      balcony: "Balkon/Terrasse",
      garden: "Garten",
      garage: "Garage",
      basement: "Keller",
      parking: "Außenstellplatz",
      elevator: "Aufzug",
    };
    return features.map((f) => featureLabels[f] || f).join(", ");
  };

  switch (stepId) {
    case "property-type":
      return formData.propertyType || "";
    case "sub-type":
      return formData.subPropertyType || "";
    case "property-size":
      if (formData.propertyType === "Wohnung") {
        return formData.livingSpace ? `${formData.livingSpace} m²` : "";
      }
      return formData.plotSize ? `${formData.plotSize} m²` : "";
    case "additional-size":
      if (formData.propertyType === "Wohnung") {
        return formData.rooms ? `${formData.rooms}` : "";
      }
      if (formData.propertyType === "Haus") {
        return formData.livingSpace ? `${formData.livingSpace} m²` : "";
      }
      if (formData.propertyType === "Gewerbe") {
        return formData.commercialArea ? `${formData.commercialArea} m²` : "";
      }
      return "";
    case "construction-year":
      return formData.constructionYear || "";
    case "property-features":
      return formatFeatures(formData.propertyFeatures);
    case "quality":
      return formData.quality || "";
    case "condition":
      return formData.condition || "";
    case "usage":
      return formData.usage || "";
    case "purpose":
      return formData.reason || "";
    case "timeline":
      return formData.timeline || "";
    case "address":
      const addressParts = [
        formData.street,
        formData.house_number,
        formData.zip_code,
        formData.city || formData.ort,
      ].filter(Boolean);
      return addressParts.length > 0 ? addressParts.join(", ") : "";
    case "contact":
      // Contact data is in separate state, not in PropertyFormData
      return "";
    default:
      return "";
  }
}

export function areAllStepsFilled(
  formData: Partial<PropertyFormData>
): boolean {
  const visibleSteps = formSteps.filter(
    (step) => !shouldSkipStep(step.id, formData) && step.id !== "contact"
  );
  return visibleSteps.every((step) => isStepFilled(step.id, formData));
}
